import type {
    DateFormat,
    FileUploadType,
    QuestionValue,
    ResidenceType
} from '@webapp/account/src/resources/questions/index';

import type { DateTimeType, TimeFormat } from '@webapp/common/lib/date';
import { getColor } from '@webapp/common/lib/ui';
import type { AnswerType } from '@webapp/common/resources/survey';
import { DropdownType } from '@webapp/common/resources/survey';

import type { SmilePackName } from '@webapp/ui/lib/smile-image/index';

export interface QuestionValues {
    value: QuestionValue;
    value2?: QuestionValue;
    value3?: QuestionValue;
}

export const QuestionValuesKeys: Set<keyof QuestionValues> = new Set(['value', 'value2', 'value3']);

export interface QuestionRow extends QuestionValues {
    id: number;

    isNew?: boolean;

    type?: AnswerType;
    exception?: boolean;
    order?: number;

    fileId: string;
    filePath: string;
    videoLink?: string;
    width: number;
    height: number;
    mimeType: string;

    text?: string;
    textBlock?: string;
}

export interface QuestionCol {
    id: number;
    name: string;
    type?: AnswerType;
    exception?: boolean | null;
    answers?: Array<string>;
    order?: number;
    numeric_value?: number;
    questionId?: number;
    isNew?: boolean;
}

export interface NestedItem {
    id: number;
    name: string;
    parentId: number;
    level: number;
    answerId: number;
}

export enum QuestionParamsModelAbType {
    TEXT = 'text',
    VIDEO = 'video',
    IMAGE = 'image'
}

export interface QuestionParams {
    ABType?: QuestionParamsModelAbType;
    additionalImage?: string;
    additionalText?: string;
    agreementLeftLabel: string;
    agreementLink: string;
    agreementRightLabel: string;
    agreementText: string;
    amount: number;
    answerType: 'any' | 'number';
    answers: Array<string>;
    cantRate?: boolean;
    cantRateText?: string;
    chartColor?: string;
    chartType?: string;
    color: string;
    comment: boolean;
    commentRequired: boolean;
    commentText: string;
    csiFormat: string;
    dateFormat: DateFormat;
    dateType: DateTimeType;
    distributeLabel: string;
    distributeScore: number;
    distributeSurplus: boolean;
    distributeUnit: string;
    dropdownListType: DropdownType;
    dropdownType: 'single' | 'multi';
    excludeSelectedAnswer: boolean;
    fileAmount: number;
    fileLimitExt: Array<string>;
    fileType: FileUploadType;
    help: boolean;
    helpText: string;
    hours: number;
    imageColumns: number;
    imageTextPosition: 'under' | 'above';
    matrixNumbersColor: Array<string>;
    matrixType: 'stars' | 'numbers' | 'numbers-with-borders' | 'slider';
    maxAmount: number;
    maxLength?: number;
    minAmount: number;
    minLength?: number;
    minutes: number;
    name: boolean; // show name. TODO ?? purge
    npsColors: Array<string>;
    npsLeftLabel: string;
    npsRightLabel: string;
    npsType: 'numbers' | 'numbers-with-borders' | 'slider';
    phoneFormatCountry: string;
    phoneFormatNumber: string;
    placeholder?: string;
    questionColumns: number;
    randomOrder: boolean;
    required: boolean;
    residence: ResidenceType;
    scaleColors: Array<string>;
    scaleEndDia: number;
    scaleFormat: 'numbers' | 'numbers-with-borders' | 'slider';
    scaleStartDia: number;
    scaleType: 'number' | 'custom';
    seconds: number;
    size: number;
    smilePack: SmilePackName;
    smileType: string;
    testScore?: number;
    textBlock: string;
    timeFormat: TimeFormat;
    timer: boolean;
    totalScore?: boolean;
    uploadButtonName: string;
}

export const initialQuestionParams: Readonly<QuestionParams> = {
    additionalText: '',
    agreementLeftLabel: null,
    agreementLink: null,
    agreementRightLabel: null,
    agreementText: null,
    amount: null,
    answerType: null,
    answers: [],
    cantRate: false,
    cantRateText: 'Не могу оценить',
    color: null,
    comment: false,
    commentRequired: false,
    commentText: '',
    csiFormat: 'numbers',
    dateFormat: null,
    dateType: null,
    distributeLabel: null,
    distributeScore: null,
    distributeSurplus: null,
    distributeUnit: null,
    dropdownListType: DropdownType.DIFFERENT,
    dropdownType: 'single',
    excludeSelectedAnswer: false,
    fileAmount: null,
    fileLimitExt: [],
    fileType: null,
    help: false,
    helpText: null,
    hours: 0,
    imageColumns: 1,
    imageTextPosition: 'under',
    matrixNumbersColor: [getColor('blue-nps')],
    matrixType: 'stars',
    maxAmount: 10,
    maxLength: null,
    minAmount: 1,
    minLength: null,
    minutes: 1,
    name: true,
    npsColors: [getColor('blue-nps')],
    npsLeftLabel: null,
    npsRightLabel: null,
    npsType: 'numbers',
    phoneFormatCountry: null,
    phoneFormatNumber: null,
    questionColumns: 1,
    randomOrder: false,
    required: true,
    residence: null,
    scaleColors: [getColor('blue-nps')],
    scaleEndDia: 5,
    scaleFormat: 'numbers',
    scaleStartDia: 1,
    scaleType: 'number',
    seconds: 0,
    size: null,
    smilePack: null,
    smileType: null,
    textBlock: null,
    timeFormat: null,
    timer: false,
    uploadButtonName: null
};
